import type {
  Competition,
  CompetitionResult,
  CompetitionWithRules,
  GetCompetitionsParams,
} from '@shared/types/entities';
import { CompetitionLevel, CompetitionType, SportType } from '@shared/enums/domain.enum';

import { api } from './client';
import { type PaginatedResponse, buildQuery } from './types';

export type { Competition, CompetitionResult, CompetitionWithRules, GetCompetitionsParams };

// ─── Payload types ──────────────────────────────────────
// Définis ici (source unique) pour être partagés entre admin et tout futur consumer.
export interface CreateCompetitionPayload {
  name: string;
  alias: string;
  type: CompetitionType;
  level: CompetitionLevel;
  sportType: SportType;
  date: string; // ISO
  registrationEnd: string; // ISO
  registrationOpensAt?: string | null; // ISO — ouverture des inscriptions (optionnel ; null = effacer)
  maxTeams: number;
  minTeams?: number;
  playersPerTeam: number;
  restrictedToMembers?: boolean;
  restrictedAffiliates?: boolean;
  price?: number; // centimes
  venue: string;
  description?: string;
  organizationId: number;
  // Géolocalisation (optionnel — autocomplete adresse)
  city?: string;
  department?: string;
  postcode?: string;
  citycode?: string;
  latitude?: number;
  longitude?: number;
}

export type UpdateCompetitionPayload = Partial<Omit<CreateCompetitionPayload, 'organizationId'>>;

// ─── Fonctions communes ─────────────────────────────────

export async function getCompetitionsApi(params?: GetCompetitionsParams): Promise<PaginatedResponse<Competition>> {
  return api.get(`/competitions${buildQuery(params as Record<string, unknown>)}`);
}

export async function getCompetitionApi(id: number): Promise<Competition> {
  return api.get(`/competitions/${id}`);
}

export async function getCompetitionWithRulesApi(id: number): Promise<CompetitionWithRules> {
  return api.get(`/competitions/${id}/with-rules`);
}

export async function getCompetitionResultsApi(id: number): Promise<CompetitionResult[]> {
  return api.get(`/competitions/${id}/results`);
}

export async function checkClubAffiliationApi(competitionId: number, clubId: number): Promise<{ affiliated: boolean }> {
  return api.get(`/competitions/${competitionId}/check-affiliation/${clubId}`);
}

// ─── Draft / Publish / Clone ────────────────────────────
// Brouillon : seuls organizationId + name sont requis. alias optionnel (auto-slug côté API si vide).
// Publication : POST /competitions/:id/publish (gated, valide les champs requis).
// Clone : POST /competitions/:id/clone (auto-suffix `-copy`, dates vidées, status DRAFT).

export interface CreateCompetitionDraftPayload {
  organizationId: number;
  name: string;
  alias?: string;
  type?: CompetitionType;
  level?: CompetitionLevel;
  sportType?: SportType;
  date?: string;
  registrationEnd?: string;
  registrationOpensAt?: string | null; // ISO — ouverture des inscriptions (optionnel ; null = effacer)
  maxTeams?: number;
  minTeams?: number;
  playersPerTeam?: number;
  restrictedToMembers?: boolean;
  restrictedAffiliates?: boolean;
  requireTeamName?: boolean;
  price?: number;
  venue?: string;
  description?: string;
  city?: string;
  department?: string;
  postcode?: string;
  citycode?: string;
  latitude?: number;
  longitude?: number;
}

export interface PublishCompetitionErrorBody {
  statusCode: 400;
  message: string;
  missingFields: string[];
}

export async function createDraftCompetitionApi(data: CreateCompetitionDraftPayload): Promise<Competition> {
  return api.post('/competitions/draft', data);
}

export async function publishCompetitionApi(id: number): Promise<Competition> {
  return api.post(`/competitions/${id}/publish`, {});
}

export async function cloneCompetitionApi(id: number): Promise<Competition> {
  return api.post(`/competitions/${id}/clone`, {});
}
